import SliderControl from './sliderControl';
import {
  ATTRIBUTE_SLIDER_SETTINGS,
  FIELD_TYPES,
} from '@helpers/slider-utils.js';

const {
  i18n: { __ },
  components: { PanelBody },
  element: { useState, useEffect, memo },
} = wp;

/**
 *
 * @param {Object}   props
 * @param {Object}   props.sliderMediaBreakPoint     { type: 'sm', breakPoint: 576, slug: 'mobile', label: 'Mobile' }
 * @param {Object}   props.sliderSettings            Slider attributes of a specific device (mobile, tablet, tablet-landscape, desktop)
 * @param {Array}    props.slides                    Number of slides in the editor
 * @param {Function} props.setSliderSettingAttribute Function for change attributes
 * @return {HTMLElement} Returns the HTML element
 */
const SliderSettings = (props) => {
  const {
    sliderMediaBreakPoint,
    sliderSettings,
    slides,
    setSliderSettingAttribute,
    removeSliderSettingAttribute,
  } = props;
  const { type, label, slug } = sliderMediaBreakPoint;
  const [sliderAutoplay, setSliderAutoplay] = useState(false);
  const [sliderSlidesPerView, setSlidesPerView] = useState(false);
  const [sliderPagination, setSliderPagination] = useState(false);
  const sliderLength = slides.length;
  const DEFAULT_AUTOPLAY_SPEED = 2500;
  const DEFAULT_SLIDES_PER_VIEW = 1;

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    sliderSettings?.autoplay
      ? setSliderAutoplay(true)
      : setSliderAutoplay(false);

    // eslint-disable-next-line no-unused-expressions
    sliderSettings.slidesPerView === 'auto'
      ? setSlidesPerView(true)
      : setSlidesPerView(false);

    // eslint-disable-next-line no-unused-expressions
    sliderSettings.pagination.enabled
      ? setSliderPagination(true)
      : setSliderPagination(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sliderSettingsFields = [
    {
      label: 'Infinite',
      name: 'loop',
      value: sliderSettings.loop,
      type: FIELD_TYPES.Toggle,
    },
    {
      label: 'Autoplay',
      name: 'autoplay',
      value: sliderAutoplay,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderAutoplay(value);
        const settingKey = 'autoplay';
        if (value === true) {
          setSliderSettingAttribute([
            {
              attribute: ATTRIBUTE_SLIDER_SETTINGS,
              layout: deviceSlug,
              property: settingKey,
              childProperty: 'delay',
              value: DEFAULT_AUTOPLAY_SPEED,
            },
            {
              attribute: ATTRIBUTE_SLIDER_SETTINGS,
              layout: deviceSlug,
              property: settingKey,
              childProperty: 'disableOnInteraction',
              value: false,
            },
            {
              attribute: ATTRIBUTE_SLIDER_SETTINGS,
              layout: deviceSlug,
              property: settingKey,
              childProperty: 'stopOnLastSlide',
              value: true,
            },
          ]);
        } else {
          removeSliderSettingAttribute(
            ATTRIBUTE_SLIDER_SETTINGS,
            deviceSlug,
            settingKey,
            '',
          );
        }
      },
    },
    {
      label: 'Autoplay Speed (milliseconds)',
      name: 'autoplay',
      value: sliderSettings.autoplay?.delay || DEFAULT_AUTOPLAY_SPEED,
      type: FIELD_TYPES.Range,
      help: 'Slide faster with lower values and slower with higher values',
      min: 100,
      max: 10000,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_SLIDER_SETTINGS,
          deviceSlug,
          'autoplay',
          'delay',
          value,
        );
      },
      shouldRenderCallback() {
        return sliderAutoplay === true;
      },
    },
    {
      label: 'Variable Width',
      name: 'slidesPerView',
      value: sliderSlidesPerView,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSlidesPerView(value);
        const settingKey = 'slidesPerView';
        if (value === true) {
          setSliderSettingAttribute(
            ATTRIBUTE_SLIDER_SETTINGS,
            deviceSlug,
            settingKey,
            '',
            'auto',
          );
        } else {
          setSliderSettingAttribute(
            ATTRIBUTE_SLIDER_SETTINGS,
            deviceSlug,
            settingKey,
            '',
            DEFAULT_SLIDES_PER_VIEW,
          );
        }
      },
    },
    {
      label: 'Slides to Show',
      name: 'slidesPerView',
      value: sliderSettings.slidesPerView,
      type: FIELD_TYPES.Number,
      step: 'any',
      min: 1,
      mix: sliderLength,
      onChange(deviceSlug, value) {
        const castedValue = parseFloat(value);
        setSliderSettingAttribute(
          ATTRIBUTE_SLIDER_SETTINGS,
          deviceSlug,
          'slidesPerView',
          '',
          !isNaN(castedValue) ? castedValue : 0,
        );
      },
      shouldRenderCallback() {
        return sliderSlidesPerView === false;
      },
    },
    {
      label: 'Slides to Scroll',
      name: 'slidesPerGroup',
      value: sliderSettings.slidesPerGroup,
      type: FIELD_TYPES.Range,
      min: 1,
      mix: sliderLength,
    },
    {
      label: 'Show Arrows',
      name: 'navigation',
      value: sliderSettings.navigation.enabled,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_SLIDER_SETTINGS,
          deviceSlug,
          'navigation',
          'enabled',
          value,
        );
      },
    },
    {
      label: 'Enable Pagination',
      name: 'pagination',
      value: sliderPagination,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderPagination(value);
        setSliderSettingAttribute(
          ATTRIBUTE_SLIDER_SETTINGS,
          deviceSlug,
          'pagination',
          'enabled',
          value,
        );
      },
    },
    {
      label: 'Pagination Type',
      name: 'pagination',
      value:
        sliderSettings.pagination.type !== undefined
          ? sliderSettings.pagination.type
          : 'dots',
      options: [
        {
          label: __('Show Dots', 'b3rg'),
          value: 'dots',
        },
        {
          label: __('Progress Bar', 'b3rg'),
          value: 'progressbar',
        },
      ],
      type: FIELD_TYPES.Select,
      onChange(deviceSlug, value) {
        const settingKey = 'type';
        if (value && value === 'dots') {
          removeSliderSettingAttribute(
            ATTRIBUTE_SLIDER_SETTINGS,
            deviceSlug,
            'pagination',
            settingKey,
          );
        }
        if (value && value === 'progressbar') {
          setSliderSettingAttribute(
            ATTRIBUTE_SLIDER_SETTINGS,
            deviceSlug,
            'pagination',
            settingKey,
            'progressbar',
          );
        }
      },
      shouldRenderCallback() {
        return sliderPagination === true;
      },
    },
    {
      label: 'Scroll Speed (milliseconds)',
      name: 'speed',
      value: sliderSettings.speed,
      type: FIELD_TYPES.Range,
      min: 1,
      max: 10000,
      help: 'Slide faster with lower values and slower with higher values',
    },
    {
      label: 'Adaptive Height',
      name: 'autoHeight',
      value: sliderSettings.autoHeight,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_SLIDER_SETTINGS,
          deviceSlug,
          'autoHeight',
          '',
          value,
        );
      },
    },
    {
      label: 'Draggable',
      name: 'allowTouchMove',
      value: sliderSettings.allowTouchMove,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_SLIDER_SETTINGS,
          deviceSlug,
          'allowTouchMove',
          '',
          value,
        );
      },
    },
    {
      label: 'Lazy Load',
      name: 'lazy',
      value: sliderSettings.lazy,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_SLIDER_SETTINGS,
          deviceSlug,
          'lazy',
          '',
          value,
        );
      },
    },
    {
      label: 'Center Mode',
      name: 'centeredSlides',
      value: sliderSettings.centeredSlides,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_SLIDER_SETTINGS,
          deviceSlug,
          'centeredSlides',
          '',
          value,
        );
      },
    },
    {
      label: 'Initial Slide',
      name: 'initialSlide',
      value: sliderSettings.initialSlide,
      type: FIELD_TYPES.Number,
      max: sliderLength,
      min: 1,
      step: 1,
      onChange(deviceSlug, value) {
        const castedValue = parseInt(value);
        setSliderSettingAttribute(
          ATTRIBUTE_SLIDER_SETTINGS,
          deviceSlug,
          'initialSlide',
          '',
          !isNaN(castedValue) ? castedValue - 1 : 0,
        );
      },
      valueFormatter(value) {
        let castedValue;

        if (typeof value === 'string') {
          castedValue = parseInt(value);
        } else if (typeof value === 'number') {
          castedValue = value;
        } else {
          castedValue = NaN;
        }

        return !isNaN(castedValue) ? castedValue + 1 : 1;
      },
    },
    {
      label: 'Space Between (pixel)',
      name: 'spaceBetween',
      value: sliderSettings.spaceBetween,
      type: FIELD_TYPES.Number,
      min: 0,
      step: 1,
      onChange(deviceSlug, value) {
        const castedValue = parseInt(value);
        setSliderSettingAttribute(
          ATTRIBUTE_SLIDER_SETTINGS,
          deviceSlug,
          'spaceBetween',
          '',
          !isNaN(castedValue) ? castedValue : 0,
        );
      },
    },
    {
      label: 'Effect',
      name: 'effect',
      value: sliderSettings.effect,
      options: [
        {
          label: __('Slide', 'b3rg'),
          value: 'slide',
        },
        {
          label: __('Fade', 'b3rg'),
          value: 'fade',
        },
      ],
      type: FIELD_TYPES.Select,
    },
    {
      label: 'Destroy Slider',
      name: 'destroy',
      value: sliderSettings.destroy,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_SLIDER_SETTINGS,
          deviceSlug,
          'destroy',
          '',
          value,
        );
      },
      help: 'Remove slider initialization',
    },
  ];

  return (
    <>
      <PanelBody title={label} initialOpen={false} key={type}>
        {sliderSettingsFields.map((sliderSettingsField) => {
          if (
            typeof sliderSettingsField.shouldRenderCallback === 'function' &&
            sliderSettingsField.shouldRenderCallback() === false
          ) {
            return null;
          }
          return (
            <SliderControl
              key={sliderSettingsField.label}
              {...{
                sliderSettingsField,
                deviceSlug: slug,
                setSliderSettingAttribute,
              }}
            />
          );
        })}
      </PanelBody>
    </>
  );
};

export default memo(SliderSettings);
